import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useRef, useState } from "react";
import { useReactFlow } from "@xyflow/react";
import { computeHelperLines } from "./helperLines";
import { HelperLinesOverlay } from "./HelperLinesOverlay";
const DEFAULT_THRESHOLD_PX = 10;
export function useHelperLines(options) {
    const { getDimensions, isCandidate, thresholdPx = DEFAULT_THRESHOLD_PX, proximityMarginY } = options;
    const [vertical, setVertical] = useState(undefined);
    const [horizontal, setHorizontal] = useState(undefined);
    // Guides in a ref so `transformChanges` stays `[]`-stable — state is only for the overlay render.
    const guidesRef = useRef({});
    const getDimensionsRef = useRef(getDimensions);
    getDimensionsRef.current = getDimensions;
    const isCandidateRef = useRef(isCandidate);
    isCandidateRef.current = isCandidate;
    const thresholdPxRef = useRef(thresholdPx);
    thresholdPxRef.current = thresholdPx;
    const proximityMarginYRef = useRef(proximityMarginY);
    proximityMarginYRef.current = proximityMarginY;
    const rf = useReactFlow();
    const clearGuides = useCallback(()=>{
        if (guidesRef.current.v === undefined && guidesRef.current.h === undefined) return;
        guidesRef.current = {};
        setVertical(undefined);
        setHorizontal(undefined);
    }, []);
    const publishGuides = useCallback((v, h)=>{
        const cur = guidesRef.current;
        if (cur.v === v && cur.h === h) return;
        guidesRef.current = {
            v,
            h
        };
        if (cur.v !== v) setVertical(v);
        if (cur.h !== h) setHorizontal(h);
    }, []);
    const transformChanges = useCallback((changes)=>{
        // Group drags ship one position change per selected node — group-bbox snapping isn't in scope.
        if (changes.length !== 1) {
            clearGuides();
            return changes;
        }
        const only = changes[0];
        // Snap the final mouseup frame (`dragging: false`) too; RF emits raw cursor coords there,
        // and without snapping the node visibly jumps off the guide. Reject only programmatic
        // moves (`dragging: undefined`).
        if (only.type !== "position" || only.dragging === undefined || !only.position) {
            clearGuides();
            return changes;
        }
        const draggedInternal = rf.getInternalNode(only.id);
        if (!draggedInternal) {
            clearGuides();
            return changes;
        }
        // RF stores parented node `position` in parent-relative coords; the incoming change follows
        // the same rule. Convert to absolute canvas space for snapping, convert back when rewriting,
        // otherwise lane children compare/render in different spaces and horizontal guides land wrong.
        let parentAbsX = 0;
        let parentAbsY = 0;
        if (draggedInternal.parentId) {
            const parent = rf.getInternalNode(draggedInternal.parentId);
            if (parent) {
                parentAbsX = parent.internals.positionAbsolute.x;
                parentAbsY = parent.internals.positionAbsolute.y;
            }
        }
        const draggedNode = draggedInternal;
        const { width: dW, height: dH } = getDimensionsRef.current(draggedNode);
        const dragging = {
            id: only.id,
            x: only.position.x + parentAbsX,
            y: only.position.y + parentAbsY,
            width: dW,
            height: dH
        };
        const filter = isCandidateRef.current;
        const candidates = [];
        for (const n of rf.getNodes()){
            if (n.id === only.id) continue;
            if (filter && !filter(n)) continue;
            const internal = rf.getInternalNode(n.id);
            if (!internal) continue;
            const { width, height } = getDimensionsRef.current(n);
            candidates.push({
                id: n.id,
                x: internal.internals.positionAbsolute.x,
                y: internal.internals.positionAbsolute.y,
                width,
                height
            });
        }
        const threshold = thresholdPxRef.current / rf.getZoom();
        const marginY = proximityMarginYRef.current?.(dragging) ?? dragging.width;
        // Default `marginY` (dragged width) keeps horizontal guides within roughly a column.
        // `marginX` deliberately unset so cross-lane vertical guides work.
        const result = computeHelperLines({
            dragging,
            candidates,
            thresholdX: threshold,
            thresholdY: threshold,
            proximity: {
                marginY
            }
        });
        // Skip publishing on the final frame — `onNodeDragStop` clears guides, and a publish+clear
        // in the same tick would briefly flash the overlay.
        if (only.dragging === true) {
            publishGuides(result.vertical, result.horizontal);
        }
        if (result.snapPosition.x === undefined && result.snapPosition.y === undefined) {
            return changes;
        }
        const snappedAbsX = result.snapPosition.x ?? only.position.x + parentAbsX;
        const snappedAbsY = result.snapPosition.y ?? only.position.y + parentAbsY;
        return [
            {
                ...only,
                position: {
                    x: snappedAbsX - parentAbsX,
                    y: snappedAbsY - parentAbsY
                }
            }
        ];
    }, [
        rf,
        clearGuides,
        publishGuides
    ]);
    return {
        transformChanges,
        overlay: /*#__PURE__*/ _jsx(HelperLinesOverlay, {
            vertical: vertical,
            horizontal: horizontal
        }),
        clearGuides
    };
}

//# sourceMappingURL=useHelperLines.js.map